<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui';
import type { StoreCreateFormObject, StoreUpdateFormObject } from '#imports';
import type { MonospaceWorkspace, ResolvedMonospaceServiceAccount, Uuid } from '#layers/data-model/public';
import type { DialogEmits } from '#layers/ui/public';
import { useEmitAsProps } from 'reka-ui';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ApiKeyList from '#layers/access/components/ApiKeys/ApiKeyList.vue';
import { useApiKeys } from '#layers/access/composables/use-api-keys';
import { useServiceAccountWorkspaces } from '#layers/access/composables/use-service-account-workspaces';
import { ButtonRemove, ListCard } from '#layers/ui/public';
import { useWorkspaceDisplayNames } from '#layers/workspace/public';

const props = defineProps<{
	isEdit?: boolean;
	form: StoreCreateFormObject<'MonospaceServiceAccount'> | StoreUpdateFormObject<'MonospaceServiceAccount'>;
	serviceAccount: ResolvedMonospaceServiceAccount | null;
	prefillWorkspaces?: MonospaceWorkspace[];
}>();

const emit = defineEmits<DialogEmits>();
const forwarded = useEmitAsProps(emit);
const { t } = useI18n();

const workspaces = computed<MonospaceWorkspace[]>(() => {
	if (!props.serviceAccount) return props.prefillWorkspaces ?? [];

	return props.serviceAccount.workspaces
		.map((member) => member.workspace)
		.filter((workspace) => workspace !== undefined);
});

const {
	availableWorkspaces,
	assignedWorkspaces,
	addWorkspace,
	removeWorkspace,
	saveChanges,
} = useServiceAccountWorkspaces(() => props.serviceAccount, workspaces);
const { getWorkspaceDisplayName } = useWorkspaceDisplayNames();

const {
	apiKeys,
	allApiKeys,
	pageNumber,
	pageSize,
	totalItemsCount,
	isLoading: apiKeysLoading,
	refresh: refreshApiKeys,
} = useApiKeys(() => ({
	filter: props.serviceAccount ? { userId: props.serviceAccount.id } : undefined,
}));

const formState = props.form;

const title = computed(() =>
	props.isEdit ? t('service_accounts.edit_service_account') : t('service_accounts.create_service_account'),
);

const confirmLabel = computed(() =>
	props.isEdit ? t('common.save') : t('common.create'),
);

const hasWorkspaceChanges = computed(() => {
	const originalIds = new Set(workspaces.value.map((p) => p.id));
	const modifiedIds = new Set(assignedWorkspaces.value.map((p) => p.id));

	if (originalIds.size !== modifiedIds.size) return true;
	return [...originalIds].some((id) => !modifiedIds.has(id));
});

const hasChanges = computed(() => props.form.$hasChanges() || hasWorkspaceChanges.value);

const workspaceDropdownItems = computed<DropdownMenuItem[]>(() => {
	const items: DropdownMenuItem[] = availableWorkspaces.value.map((workspace) => ({
		label: getWorkspaceDisplayName(workspace),
		onSelect: () => handleAddWorkspace(workspace.id),
	}));

	return items;
});

function handleAddWorkspace(workspaceId: Uuid) {
	const workspace = availableWorkspaces.value.find((p) => p.id === workspaceId);

	if (workspace) {
		addWorkspace(workspace);
	}
}

async function handleSubmit() {
	const serviceAccount = await props.form.$submit();
	await saveChanges(serviceAccount.id);

	emit('close', true);
}
</script>

<template>
	<DDialog
		v-bind="forwarded"
		:title
		:confirm="{
			label: confirmLabel,
			disabled: !hasChanges,
			loading: form.$loading,
			onClick: handleSubmit,
		}"
		:ui="{ content: 'sm:max-w-2xl' }"
	>
		<template #body>
			<DForm
				:state="form"
				:schema="form.$schema"
				layout="stacked"
				:validate-on="[]"
				auto-focus-first-input
				@submit="handleSubmit"
			>
				<DFormField
					name="fullName"
					:label="$t('common.name')"
					required
				>
					<DInput
						v-model="formState.fullName"
						:placeholder="$t('service_accounts.name_placeholder')"
					/>
				</DFormField>

				<DFormField :label="$t('service_accounts.workspaces')">
					<ListCard
						:items="assignedWorkspaces"
						size="xs"
						:ui="{
							item: {
								base: 'cursor-auto pr-2',
								label: 'font-medium',
							},
						}"
					>
						<template #header>
							<span v-if="assignedWorkspaces.length > 0" class="text-xs font-medium text-toned">{{ $t('common.n_items', assignedWorkspaces.length) }}</span>
							<span v-else class="text-xs font-medium text-toned">{{ $t('service_accounts.no_workspaces') }}</span>
						</template>
						<template #header-actions>
							<DTooltip :text="$t('service_accounts.no_workspaces_available')" :disabled="workspaceDropdownItems.length > 0">
								<!-- TODO: div necessary because Nuxt UI tooltip doesnt't play nice with dropdown menu -->
								<div>
									<DDropdownMenu
										:items="workspaceDropdownItems"
										:ui="{ item: 'min-w-32' }"
									>
										<DButton
											color="neutral"
											variant="ghost"
											size="sm"
											icon="lucide:plus"
											:disabled="workspaceDropdownItems.length === 0"
										>
											{{ $t('service_accounts.add_workspace') }}
										</DButton>
									</DDropdownMenu>
								</div>
							</DTooltip>
						</template>
						<template #item-label="{ item }">
							{{ getWorkspaceDisplayName(item) }}
						</template>

						<template #item-trailing="{ item }">
							<ButtonRemove
								class="opacity-0 group-hover/list-item:opacity-100 group-focus-visible/list-item:opacity-100"
								with-leading-icon
								:trash-bin="false"
								square
								@click="removeWorkspace(item)"
							/>
						</template>
					</ListCard>
				</DFormField>

				<DFormField v-if="serviceAccount" :label="$t('organization.dashboard.settings.access.api_keys.title')">
					<ApiKeyList
						v-model:page="pageNumber"
						v-model:page-size="pageSize"
						:api-keys
						:all-api-keys
						:total-items-count
						:is-loading="apiKeysLoading"
						:service-account
						@refresh="refreshApiKeys"
					/>
				</DFormField>
			</DForm>
		</template>
	</DDialog>
</template>
